import React from 'react';
import FallbackImage from '../FallbackImage/FallbackImage';

const ProductVariations = ({ product, variations }) => {
  const hasNutrition = Boolean(product.nutrition);

  return (
    <div className="max-w-7xl mx-auto px-6 py-16">
      {/* First Row - Product Title and Description */}
      <div className="text-center mb-16">
        <h2 className="text-4xl md:text-5xl font-bold text-[#0B1E2B] mb-6">
          {product.title}
        </h2>
        <p className="text-lg text-gray-700 leading-relaxed max-w-4xl mx-auto">
          {product.description}
        </p>
      </div>

      {/* Second Row - Image Left, Variations Right */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start mb-16">
        {/* Left Side - Product Image */}
        <div className="relative">
          <div className="aspect-square bg-[#F3F4F6] rounded-2xl overflow-hidden shadow-lg">
            <FallbackImage
              src={`/media/${encodeURI(product.image)}`}
              alt={product.title}
              className="w-full h-full object-contain"
              placeholderText={product.title}
            />
          </div>
        </div>

        {/* Right Side - Available Variations */}
        <div>
          <h3 className="text-2xl font-bold text-[#0B1E2B] mb-6">
            Available Variations
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {variations.map((variation, index) => (
              <div 
                key={index}
                className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-md transition-shadow duration-300"
              >
                <h4 className="text-lg font-bold text-[#0B1E2B] mb-3">
                  {variation.name}
                </h4>
                <div className="space-y-2 mb-4">
                  {variation.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#FF6B35]"></div>
                      <span className="text-gray-600 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
                <div className="pt-3 border-t border-gray-100">
                  <span className="inline-block bg-[#FF6B35] text-white px-3 py-1 rounded-full text-xs font-semibold">
                    {variation.meta.weight}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Third Row - Nutrition + Key Features */}
      {hasNutrition ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Nutrition Facts - Left */}
          <div className="rounded-2xl border border-gray-200 bg-white shadow-lg overflow-hidden">
            <div className="px-6 py-4 bg-gradient-to-r from-[#FF6B35] to-[#e85a2b]">
              <h3 className="text-2xl font-bold text-white">Nutrition Facts</h3>
            </div>
            <div className="p-6">
              <dl className="divide-y divide-gray-200 text-[#0B1E2B]">
                <div className="grid grid-cols-2 gap-3 py-3">
                  <dt className="font-semibold">Serving size</dt>
                  <dd className="text-right text-gray-700">{product.nutrition.servingSize}</dd>
                </div>
                <div className="grid grid-cols-2 gap-3 py-3">
                  <dt className="font-semibold">Calories</dt>
                  <dd className="text-right text-gray-700">{product.nutrition.calories}</dd>
                </div>
                <div className="grid grid-cols-2 gap-3 py-3">
                  <dt className="font-semibold">Fat</dt>
                  <dd className="text-right text-gray-700">{product.nutrition.fat}</dd>
                </div>
                <div className="grid grid-cols-2 gap-3 py-3">
                  <dt className="font-semibold">Carbs</dt>
                  <dd className="text-right text-gray-700">{product.nutrition.carbs}</dd>
                </div>
                <div className="grid grid-cols-2 gap-3 py-3">
                  <dt className="font-semibold">Fiber</dt>
                  <dd className="text-right text-gray-700">{product.nutrition.fiber}</dd>
                </div>
                <div className="grid grid-cols-2 gap-3 py-3">
                  <dt className="font-semibold">Protein</dt>
                  <dd className="text-right text-gray-700">{product.nutrition.protein}</dd>
                </div>
                <div className="grid grid-cols-2 gap-3 py-3">
                  <dt className="font-semibold">Sodium</dt>
                  <dd className="text-right text-gray-700">{product.nutrition.sodium}</dd>
                </div>
                <div className="grid grid-cols-2 gap-3 py-3">
                  <dt className="font-semibold">Potassium</dt>
                  <dd className="text-right text-gray-700">{product.nutrition.potassium}</dd>
                </div>
              </dl>
              {product.nutrition.note && (
                <div className="mt-4 text-xs text-orange-600 bg-orange-50 border border-orange-200 rounded px-3 py-2">
                  {product.nutrition.note}
                </div>
              )}
            </div>
          </div>

          {/* Key Features - Right */}
          <div className="rounded-2xl border border-gray-200 bg-white shadow-lg overflow-hidden">
            <div className="px-6 py-4 bg-gradient-to-r from-[#FF6B35] to-[#e85a2b]">
              <h3 className="text-2xl font-bold text-white">Key Features</h3>
            </div>
            <div className="p-6">
              <ul className="divide-y divide-gray-200">
                {product.mainFeatures?.map((feature, index) => (
                  <li key={index} className="py-3 flex items-start gap-3 text-[#0B1E2B]">
                    <span className="mt-1 w-2 h-2 rounded-full bg-[#FF6B35] flex-shrink-0"></span>
                    <span className="font-medium">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      ) : (
        <div className="rounded-2xl border border-gray-200 bg-white shadow-lg overflow-hidden">
          <div className="px-6 py-4 bg-gradient-to-r from-[#FF6B35] to-[#e85a2b] text-center">
            <h3 className="text-2xl font-bold text-white">Key Features</h3>
          </div>
          <div className="p-6">
            <ul className="max-w-4xl mx-auto divide-y divide-gray-200">
              {product.mainFeatures?.map((feature, index) => (
                <li key={index} className="py-3 flex items-start gap-3 text-[#0B1E2B]">
                  <span className="mt-1 w-2 h-2 rounded-full bg-[#FF6B35] flex-shrink-0"></span>
                  <span className="font-medium">{feature}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductVariations;
